class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // AUREN fix: guard against missing overlay element (was throwing 'cannot read properties of null (reading addEventListener)' when the cart-drawer element construction ran before/without the overlay markup present, breaking the whole drawer including checkout).
    const cartDrawerOverlay = this.querySelector('#CartDrawer-Overlay');
    if (cartDrawerOverlay) cartDrawerOverlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // AJAX Form Interceptor: Prevents redirect to /cart
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('form');
      if (!form) return;
      
      // Match Dawn's add-to-cart form pattern
      if (form.action.includes('/cart/add') || form.querySelector('[name="id"]')) {
        event.preventDefault();
        this.addRecommendationToCart(form);
      }
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // If update comes from outside (e.g., product page product form), open drawer
      if (event.source !== 'cart-drawer' && event.source !== 'cart-drawer-recs' && event.source !== 'cart-items') {
        this.renderContents(event.cartData);
        return;
      }
      
      // Re-initialize slider after AJAX update
      setTimeout(() => {
        this.initRecsSwiper();
      }, 500); 
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('.site-header #cart-icon-bubble, #cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // Init Swiper after drawer slide-in animation completes (~400ms)
    setTimeout(() => {
      this.initRecsSwiper();
    }, 500);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    
    const sectionsToRender = this.getSectionsToRender();
    const hasRequiredSections = parsedState.sections && sectionsToRender.every(s => parsedState.sections[s.section]);

    // Fallback: If parsedState doesn't have sections or missing core drawer section
    if (!hasRequiredSections) {
      console.log('CartDrawer: Missing required sections in response, triggering fallback fetch...');
      fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceInner = html.querySelector('.drawer__inner');
          const targetInner = this.querySelector('.drawer__inner');
          
          if (sourceInner && targetInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
            this.classList.toggle('is-empty', sourceInner.classList.contains('is-empty') || targetInner.querySelector('.drawer__inner-empty') !== null);
          }
          
          // Also update header bubble
          this.updateHeaderBubble();
          
          if (!this.classList.contains('active')) this.open();
          this.initRecsSwiper(); 
        })
        .catch((e) => console.error('Fallback Fetch Error:', e));
      return;
    }

    sectionsToRender.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHtml = parsedState.sections[section.section || section.id];
      if (!sectionHtml) return;

      const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      sectionElement.innerHTML = innerHTML;

      // Auto-detect empty state from rendered content if item_count is unreliable
      if (section.id === 'CartDrawer') {
        const isEmpty = innerHTML.includes('drawer__inner-empty');
        this.classList.toggle('is-empty', isEmpty);
      }
    });

    // Fallback: If item_count is provided explicitly, use it to override
    if (parsedState.item_count !== undefined) {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    } 
    // If it's an 'add' response (has id but maybe no item_count), it's definitely not empty
    else if (parsedState.id) {
      this.classList.remove('is-empty');
    }

    if (!this.classList.contains('active')) this.open();

    setTimeout(() => {
      // AUREN fix: same null-guard as constructor above.
      const overlayAfterRender = this.querySelector('#CartDrawer-Overlay');
      if (overlayAfterRender) overlayAfterRender.addEventListener('click', this.close.bind(this));
      this.initRecsSwiper(); // Re-init after rendering
    });
  }

  updateHeaderBubble() {
    fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) {
          target.innerHTML = source.innerHTML;
        }
      });
  }

  addRecommendationToCart(form) {
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.classList.add('loading');
      submitButton.setAttribute('aria-disabled', true);
    }

    const formData = new FormData(form);
    formData.append('sections', this.getSectionsToRender().map((section) => section.section));
    formData.append('sections_url', window.location.pathname);

    fetch(`${window.routes?.cart_add_url || '/cart/add'}`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        console.error('Cart Error:', response.description);
        return;
      }
      this.renderContents(response);
      // Notify other components
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-recs', cartData: response });
      }
    })
    .catch((e) => {
      console.error('AJAX Add Error:', e);
    })
    .finally(() => {
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      }
    });
  }

  initRecsSwiper() {
    // Destroy any existing instance first
    if (this._recsSwiper) {
      try { this._recsSwiper.destroy(true, true); } catch(e) {}
      this._recsSwiper = null;
    }
    const el = this.querySelector('#DrawerRecsSwiper');
    if (!el || typeof Swiper === 'undefined') return;
    
    // Check if element is visible and has width
    if (el.offsetWidth === 0) {
      // If not yet visible, retry once drawer is active
      return;
    }

    this._recsSwiper = new Swiper(el, {
      slidesPerView: 1.25,
      spaceBetween: 12,
      grabCursor: true,
      navigation: {
        nextEl: '#DrawerRecsNext',
        prevEl: '#DrawerRecsPrev',
      },
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      watchOverflow: true,
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
